'use strict';
//***********************************************************************************
//***********************************************************************************
//**** Resize handler
//***********************************************************************************
//***********************************************************************************

import { cn_event_handler } from './cn_event_handler';
import { cn_mouse_event } from './cn_mouse_event';
import {
    cn_mul,
    cnx_add,
    cnx_dot,
    cnx_mul,
    cnx_sub
} from '../utils/cn_utilities';
import { cn_camera } from './cn_camera';
import { cn_handler_object } from './cn_handler_object';
import { cn_handler_single_resize } from './cn_handler_single_resize';
import { cn_pastille } from './cn_pastille';

export class cn_handler_resize extends cn_event_handler {
    //*****************************************************************
    /**
     * Constructor
     * @param {cn_handler_object} obj
     * @param {cn_event_handler} parent
     */
    constructor(obj, parent) {
        super(parent);
        this.object = obj;
        this._mouseover = -1;

        this.resize_handles = [];
        for (var i = 0; i < 4; i++)
            this.resize_handles.push(new cn_handler_single_resize([0, 0, 0], [0, 0, 0], this));
        this.resize_handles[0].on("move", (p) => { return this._resize_handler(p, 0); });
        this.resize_handles[1].on("move", (p) => { return this._resize_handler(p, 1); });
        this.resize_handles[2].on("move", (p) => { return this._resize_handler(p, 2); });
        this.resize_handles[3].on("move", (p) => { return this._resize_handler(p, 3); });

        this._rectangle_box = new cn_pastille([0, 0, 0]);
        this._rectangle_box.rectangle = [80, 30];
        this._rectangle_box.svg_class = 'pastille_background white';
        this._rectangle_box.text_class = 'pastille_text black';
        this._rectangle_box.offset = [0, 50];

        this.rectangle_box = true;
    }

    _resize_handler(p, index) {
        let cosa = Math.cos(this.object.angle);
        let sina = Math.sin(this.object.angle);
        if (index < 2) {
            let dx = [cosa, sina, 0];
            let xc = cnx_dot(dx, this.object.center);
            let xx = [xc - this.object.width / 2, xc + this.object.width / 2];
            xx[index] = cnx_dot(dx, p);
            if (xx[1] - xx[0] < 0.1) {
                if (index == 0) xx[0] = xx[1] - 0.1;
                else xx[1] = xx[0] + 0.1;
            }
            this.object.width = xx[1] - xx[0];
            this.object.center = cnx_add(this.object.center, cn_mul(dx, 0.5 * (xx[0] + xx[1]) - xc));
        }
        else {
            let dy = [-sina, cosa, 0];
            let yc = cnx_dot(dy, this.object.center);
            let yy = [yc - this.object.height / 2, yc + this.object.height / 2];
            yy[index - 2] = cnx_dot(dy, p);
            if (yy[1] - yy[0] < 0.1) {
                if (index == 2) yy[0] = yy[1] - 0.1;
                else yy[1] = yy[0] + 0.1;
            }
            this.object.height = yy[1] - yy[0];
            this.object.center = cnx_add(this.object.center, cn_mul(dy, 0.5 * (yy[0] + yy[1]) - yc));
        }
        this.object.update();
        this.call("change");
        return true;
    }

    /**
     * Possibility to inactivate a resize handle.
     * 0 = x-
     * 1 = x+
     * 2 = y-
     * 3 = y+
     * @param {number} index
     */
    set_inactive_handle(index) {
        this.resize_handles.forEach((r, i) => {
            r._active = (i != index);
        })
    }

    /**
     * Possibility to inactivate a resize handle.
     * 0 = x-
     * 1 = x+
     * 2 = y-
     * 3 = y+
     * @param {number} index
     * @param {boolean} activity
     */
    set_handle_activity(index, activity) {
        if (index >= 0 && index < 4) this.resize_handles[index]._active = activity;
    }

    //*****************************************************************
    /**
     * Draw the handler
     * @param {cn_camera} camera
     * @returns {string}
     */
    draw(camera) {
        let html = "";

        /** Draw resize handles */
        let cosa = Math.cos(this.object.angle);
        let sina = Math.sin(this.object.angle);
        let dx = cnx_mul([cosa, sina, 0], 0.5 * this.object.width);
        let dy = cnx_mul([-sina, cosa, 0], 0.5 * this.object.height);
        let p0 = cnx_sub(this.object.center, cnx_add(dx, dy));
        let p1 = cnx_add(this.object.center, cnx_sub(dx, dy));
        let p2 = cnx_add(this.object.center, cnx_add(dx, dy));
        let p3 = cnx_sub(this.object.center, cnx_sub(dx, dy));
        this.resize_handles[0].p0 = p0;
        this.resize_handles[0].p1 = p3;
        this.resize_handles[1].p0 = p1;
        this.resize_handles[1].p1 = p2;
        this.resize_handles[2].p0 = p0;
        this.resize_handles[2].p1 = p1;
        this.resize_handles[3].p0 = p2;
        this.resize_handles[3].p1 = p3;
        if (!this.locked) {
            this.resize_handles.forEach(rh => {
                let pp0 = camera.world_to_screen(rh.p0);
                let pp1 = camera.world_to_screen(rh.p1);
                if (pp0 && pp1)
                    html += `<line class="" x1="${pp0[0]}" y1="${pp0[1]}" x2="${pp1[0]}" y2="${pp1[1]}" />`;
                html += rh.draw(camera);
            });
        }

        if (this.rectangle_box) {
            this._rectangle_box.position = this.object.center;
            this._rectangle_box.label = this.object.width.toFixed(2) + ' x ' + this.object.height.toFixed(2);
            html += this._rectangle_box.draw(camera);
        }

        return html;
    }

    //*****************************************************************
    /**
     * Clear move data
     */
    clear_move() {
        this._mouseover = -1;
        this.resize_handles.forEach(rh => rh.clear_move());
        this._rectangle_box.mouseover = false;
    }

    /**
     * Click event
     * @param {cn_mouse_event} ev
     * @returns  {boolean}
     */
    click(ev) {
        if (this._rectangle_box.mouseover)
            this.call("resize_label_clicked");
        return true;
    }

    /**
     * Manage a passive move. To return 'true' if something of interest under the mouse.
     * @param {cn_mouse_event} ev
     * @returns  {boolean}
     */
    move(ev) {
        this.clear_move();

        //*** Is mouse over size label ?  */
        this._rectangle_box.mouseover = this.rectangle_box && this._rectangle_box.contains_screen(ev.mouse_screen, ev.camera);
        if (this._rectangle_box.mouseover) return true;

        //*** Is mouse over a resize handle ?  */
        if (!this.locked) {
            for (var i = 0; i < this.resize_handles.length; i++) {
                if (this.resize_handles[i].move(ev)) {
                    this._mouseover = i;
                    return true;
                }
            }
        }

        //** is moue over a handle ?
        return false;
    }

    /**
     * Manage a grab. To return 'true' if grab is to be managed.
     * @param {cn_mouse_event} ev
     * @returns  {boolean}
     */
    grab(ev) {
        if (this._mouseover < 0) return false;
        return this.resize_handles[this._mouseover].grab(ev);
    }

    /**
     * Manage a drag. Only after a grab that returned true. To return 'true' if drag had an effect.
     * @param {cn_mouse_event} ev
     * @returns  {boolean}
     */
    drag(ev) {
        if (this._mouseover < 0) return false;
        return this.resize_handles[this._mouseover].drag(ev);
    }
}

